import { PaginationSchema } from '@visep/shared-types/common';
import type { Pagination } from '@visep/shared-types/common';
import { z } from 'zod';
import {
  CategoriaSchema,
  ClienteOptionSchema,
  ClienteSchema,
  ContratoSchema,
  CreateCategoriaRequestSchema,
  CreateClienteRequestSchema,
  CreateSucursalRequestSchema,
  SucursalOptionSchema,
  SucursalSchema,
  UpdateCategoriaRequestSchema,
  UpdateClienteRequestSchema,
  UpdateSucursalRequestSchema,
} from '@visep/shared-types/catalog';
import type {
  Categoria,
  CategoriaTipo,
  ClienteOption,
  Contrato,
  SucursalOption,
} from '@visep/shared-types/catalog';
import type { Helpers, ListResult } from './internal.js';
import { listResultSchema, makeCrudResource, toQuery } from './internal.js';

export type CategoriaListQuery = Pagination & { tipo?: CategoriaTipo };

export function createSucursalesResource({ request }: Helpers) {
  const crud = makeCrudResource(request, {
    basePath: '/api/sucursales',
    schema: SucursalSchema,
    listQuerySchema: PaginationSchema,
    createSchema: CreateSucursalRequestSchema,
    updateSchema: UpdateSucursalRequestSchema,
  });
  return {
    ...crud,
    // F9: shape mínimo id/nombre p/ dropdowns cross-módulo (guard só-autenticado).
    async options(query?: Pagination): Promise<ListResult<SucursalOption>> {
      const parsed = query ? PaginationSchema.parse(query) : undefined;
      return listResultSchema(SucursalOptionSchema).parse(
        await request('GET', `/api/sucursales/options${parsed ? toQuery(parsed) : ''}`),
      );
    },
  };
}

export function createClientesResource({ request, requestMultipart, requestBlob }: Helpers) {
  const crud = makeCrudResource(request, {
    basePath: '/api/clientes',
    schema: ClienteSchema,
    listQuerySchema: PaginationSchema,
    createSchema: CreateClienteRequestSchema,
    updateSchema: UpdateClienteRequestSchema,
  });
  return {
    ...crud,
    // F9: shape mínimo id/nombre p/ dropdowns cross-módulo (guard só-autenticado).
    async options(query?: Pagination): Promise<ListResult<ClienteOption>> {
      const parsed = query ? PaginationSchema.parse(query) : undefined;
      return listResultSchema(ClienteOptionSchema).parse(
        await request('GET', `/api/clientes/options${parsed ? toQuery(parsed) : ''}`),
      );
    },
    async uploadAvatar(id: string, file: Blob | File): Promise<{ avatarUrl: string }> {
      const form = new FormData();
      // filename obrigatório p/ @fastify/multipart reconhecer como arquivo (Blob puro
      // sem nome → missing_file 422). Ver uploads.image.
      form.append('file', file, file instanceof File ? file.name : 'upload');
      return (await requestMultipart(`/api/clientes/${encodeURIComponent(id)}/avatar`, form)) as {
        avatarUrl: string;
      };
    },
    async listContratos(clienteId: string): Promise<Contrato[]> {
      const data = await request('GET', `/api/clientes/${encodeURIComponent(clienteId)}/contratos`);
      return z.object({ items: z.array(ContratoSchema) }).parse(data).items;
    },
    async uploadContrato(clienteId: string, file: Blob | File): Promise<Contrato> {
      const form = new FormData();
      form.append('file', file, file instanceof File ? file.name : 'contrato');
      return ContratoSchema.parse(
        await requestMultipart(`/api/clientes/${encodeURIComponent(clienteId)}/contratos`, form),
      );
    },
    async downloadContrato(clienteId: string, contratoId: string): Promise<Blob> {
      return requestBlob(
        `/api/clientes/${encodeURIComponent(clienteId)}/contratos/${encodeURIComponent(contratoId)}/download`,
      );
    },
    async deleteContrato(clienteId: string, contratoId: string): Promise<void> {
      await request(
        'DELETE',
        `/api/clientes/${encodeURIComponent(clienteId)}/contratos/${encodeURIComponent(contratoId)}`,
      );
    },
  };
}

export function createCategoriasResource({ request }: Helpers) {
  const crud = makeCrudResource(request, {
    basePath: '/api/categorias',
    schema: CategoriaSchema,
    listQuerySchema: PaginationSchema,
    createSchema: CreateCategoriaRequestSchema,
    updateSchema: UpdateCategoriaRequestSchema,
  });
  return {
    ...crud,
    async list(query?: CategoriaListQuery): Promise<ListResult<Categoria>> {
      const parsed = PaginationSchema.parse(query ?? {});
      const merged = { ...parsed, tipo: query?.tipo };
      return listResultSchema(CategoriaSchema).parse(
        await request('GET', `/api/categorias${toQuery(merged)}`),
      );
    },
  };
}
